import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MapPin, Plus, Trash2, Video } from 'lucide-react'
import { apiGet, apiPatch, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { cn } from '@/lib/utils'
import { qk } from '@/api/query-keys'
import { toDateTimeInput } from '@/lib/dates'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CLIENT_STAGE_LABEL } from '@/components/composites/status-badge'
import { SearchSelect } from '@/components/composites/search-select'
import { localTimeZone, timeZoneOptions, zonedDateTimeToIso } from '@/lib/timezone'
import { UserAvatar } from '@/components/composites/user-avatar'
import type { Client, ClientContact, ClientStage, Geography, GeographyMode } from '@/types/entities'
import { AllowedDomainsField } from './AllowedDomainsField'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { deleteLocalDraft, newDraftId, saveLocalDraft } from '@/features/drafts/draftStore'

const STAGES: ClientStage[] = ['pilot', 'implementation', 'post_sales']

const STAGE_HINT: Record<ClientStage, string> = {
  pilot: 'Trying us out. Scope is small and the bar for saying yes is high.',
  implementation: 'Rolling out. The bulk of delivery work sits here.',
  post_sales: 'Live and supported. Hypercare and additional requirements.',
}

/**
 * Add or edit a client.
 *
 * One dialog for both, for the same reason `ProjectDialog` is one: the fields are identical,
 * and two of them drift. Creating is idempotent on the name server-side — asking for a client
 * that already exists returns the existing one rather than a second row — which is why the
 * form says so instead of pretending a duplicate name is an error to be prevented here.
 *
 * The rules this form does NOT enforce: whether a domain is already claimed by another client,
 * and whether the account owner is a real member. Both are the backend's, and both come back
 * as a message naming the actual clash, which is rendered verbatim beside the submit button.
 */
/** A geography typed on the form but not yet written — a new client has no id to hang it on. */
interface DraftGeography {
  name: string
  mode: GeographyMode
  address: string
}

export interface ClientDraftPayload {
  name: string
  stage: ClientStage | ''
  accountOwnerId: string
  nextMeetingAt: string
  meetingZone: string
  contacts: ClientContact[]
  geographies: DraftGeography[]
  allowedDomains: string[]
  notes: string
}

export function ClientDialog({
  client,
  members,
  trigger,
  open: openProp,
  onOpenChange,
  onCreated,
  draftId: draftIdProp,
  draftPayload,
  onDraftClosed,
}: {
  /** Absent = create. Present = edit that client. */
  client?: Client
  /**
   * Only enough of a person to offer them as the account owner — id, name, picture.
   *
   * Narrowed from `Member[]` so a caller can pass the roster it already holds rather than
   * fetching a second, fuller copy of the same people to satisfy a type.
   */
  members: Array<{ id: string; displayName: string; photoUrl?: string | null }>
  /** Omit when opening this from somewhere else — see `open`. */
  trigger?: ReactNode
  /**
   * Controlled, for opening this on top of another form.
   *
   * A project needs a client, and a client that does not exist yet should not mean abandoning
   * a half-filled project. `onCreated` hands the new client straight back so it is already
   * selected when this closes.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onCreated?: (client: Client) => void
  draftId?: string
  draftPayload?: ClientDraftPayload
  onDraftClosed?: () => void
}) {
  const queryClient = useQueryClient()
  const { userId } = useIdentity()
  const { workspaceId } = useWorkspace()
  const [draftId, setDraftId] = useState(() => draftIdProp ?? newDraftId())
  const [openState, setOpenState] = useState(Boolean(draftPayload))
  const open = openProp ?? openState
  const setOpen = onOpenChange ?? setOpenState
  const [tried, setTried] = useState(false)

  const [name, setName] = useState(client?.name ?? draftPayload?.name ?? '')
  const [stage, setStage] = useState<ClientStage | ''>(client?.stage ?? draftPayload?.stage ?? '')
  const [accountOwnerId, setAccountOwnerId] = useState(client?.accountOwnerId ?? draftPayload?.accountOwnerId ?? '')
  const [nextMeetingAt, setNextMeetingAt] = useState(client ? toDateTimeInput(client.nextMeetingAt) : (draftPayload?.nextMeetingAt ?? ''))
  const [meetingZone, setMeetingZone] = useState(draftPayload?.meetingZone ?? localTimeZone())
  const [contacts, setContacts] = useState<ClientContact[]>(client?.contacts ?? draftPayload?.contacts ?? [])
  /**
   * Geographies belong to the client, so on a NEW client they cannot be written until it
   * exists. They are collected here and created immediately after it saves — the only way to
   * offer them on the form that actually creates the client.
   */
  const [geographies, setGeographies] = useState<DraftGeography[]>(draftPayload?.geographies ?? [])

  // What the client already has, when editing. Read-only here: this dialog is about the client.
  const geographiesQuery = useQuery({
    queryKey: qk.geographies(client?.id),
    queryFn: () =>
      apiGet<{ items: Geography[] }>('geographies', { clientId: client?.id }).then((r) => r.items),
    enabled: open && Boolean(client?.id),
    staleTime: 60_000,
  })
  const existingGeographies = geographiesQuery.data ?? []
  const [allowedDomains, setAllowedDomains] = useState<string[]>(client?.allowedDomains ?? draftPayload?.allowedDomains ?? [])
  const [notes, setNotes] = useState(client?.notes ?? draftPayload?.notes ?? '')

  const reset = () => {
    setTried(false)
    setName(client?.name ?? '')
    setStage(client?.stage ?? '')
    setAccountOwnerId(client?.accountOwnerId ?? '')
    setNextMeetingAt(toDateTimeInput(client?.nextMeetingAt))
    setContacts(client?.contacts ?? [])
    setGeographies([])
    setAllowedDomains(client?.allowedDomains ?? [])
    setNotes(client?.notes ?? '')
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        stage,
        accountOwnerId,
        // The exact instant that was picked, in the reader's own timezone.
        // Read in the chosen zone rather than the browser's. A meeting booked from another
        // office was previously stored as that clock time HERE, which is a different moment.
        nextMeetingAt: nextMeetingAt
          ? zonedDateTimeToIso(
              nextMeetingAt.slice(0, 10),
              nextMeetingAt.slice(11, 16),
              meetingZone,
            )
          : null,
        // A contact with no name is a half-typed row, not a contact.
        contacts: contacts
          .filter((c) => c.name.trim())
          .map((c) => ({
            name: c.name.trim(),
            role: c.role.trim(),
            email: c.email?.trim().toLowerCase() ?? '',
            portalAccess: c.portalAccess === true,
          })),
        allowedDomains,
        notes: notes.trim(),
      }
      return client
        ? apiPatch<Client>(`clients/${client.id}`, { ...body, version: client.version })
        : apiPost<Client>('clients', body)
    },
    onSuccess: async (saved) => {
      // Written after the client, because before it there is no id to attach them to. A row
      // with no name is half-typed, exactly as with contacts.
      for (const g of geographies.filter((row) => row.name.trim())) {
        try {
          await apiPost<Geography>('geographies', {
            clientId: saved.id,
            name: g.name.trim(),
            mode: g.mode,
            address: g.mode === 'live' ? g.address.trim() || null : null,
          })
        } catch (error) {
          // The client saved; name the geography that did not rather than losing the lot.
          toast.error(`Saved the client, but “${g.name.trim()}” failed: ${getErrorMessage(error)}`)
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['geographies'] })
      toast.success(client ? `Saved “${saved.name}”` : `Added “${saved.name}”`)
      void queryClient.invalidateQueries({ queryKey: qk.clients() })
      void queryClient.invalidateQueries({ queryKey: qk.client(saved.id) })
      // Hand it back so whoever opened this has it selected already — the same courtesy the
      // geography field inside the project dialog does.
      if (!client) onCreated?.(saved)
      if (!client && userId) deleteLocalDraft({ id: draftId, kind: 'client', userId, workspaceId })
      onDraftClosed?.()
      setOpen(false)
    },
    // Not a toast: the dialog stays open on failure, so the backend's message — "the domain
    // cnx.com is already claimed by …" — belongs next to the button that just failed.
  })

  const missing = {
    name: !name.trim(),
    stage: !stage,
    accountOwnerId: !accountOwnerId,
  }
  const portalContactMissingEmail = contacts.some((contact) => contact.portalAccess && !contact.email?.trim())
  const complete = !Object.values(missing).some(Boolean) && !portalContactMissingEmail
  const flag = (key: keyof typeof missing) => tried && missing[key]

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-xl">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            setTried(true)
            if (complete) save.mutate()
          }}
        >
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{client ? 'Edit client' : 'New client'}</DialogTitle>
            <DialogDescription>
              {client
                ? 'A client is org-wide — these changes apply in every workspace.'
                : 'A company we work with. Projects name a client, and work items inherit it from their project.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 overflow-y-auto px-6 py-5">
            <div className="grid gap-1.5">
              <Label htmlFor="cl-name">
                Name <Required />
              </Label>
              <Input
                id="cl-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Concentrix"
                maxLength={200}
                aria-invalid={flag('name')}
              />
              <Hint show={flag('name')}>Give the client a name.</Hint>
              {!client && !flag('name') ? (
                <p className="text-tiny text-text-muted">
                  Adding a name that already exists opens that client rather than making a
                  second one.
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>
                  Stage <Required />
                </Label>
                <Select value={stage} onValueChange={(v) => setStage(v as ClientStage)}>
                  <SelectTrigger aria-invalid={flag('stage')}>
                    {/* Label only when closed; the label + hint pair is for the open list. */}
                    <SelectValue placeholder="Where is this relationship?">
                      {stage ? CLIENT_STAGE_LABEL[stage] : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        <span className="flex flex-col">
                          <span>{CLIENT_STAGE_LABEL[s]}</span>
                          <span data-slot="item-hint" className="text-tiny text-text-muted">{STAGE_HINT[s]}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Hint show={flag('stage')}>Pick the stage.</Hint>
              </div>

              <div className="grid gap-1.5">
                <Label>
                  Account owner <Required />
                </Label>
                <Select value={accountOwnerId} onValueChange={setAccountOwnerId}>
                  <SelectTrigger aria-invalid={flag('accountOwnerId')}>
                    <SelectValue placeholder="Who owns this relationship?" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="flex items-center gap-2">
                          <UserAvatar person={m} size="xs" />
                          {m.displayName}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Hint show={flag('accountOwnerId')}>
                  One person on our side, never a team.
                </Hint>
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="cl-meeting">Next meeting</Label>
              <Input
                id="cl-meeting"
                type="datetime-local"
                value={nextMeetingAt}
                onChange={(e) => setNextMeetingAt(e.target.value)}
                className="num"
              />
              <SearchSelect
                id="cl-meeting-tz"
                options={timeZoneOptions()}
                value={meetingZone}
                onChange={setMeetingZone}
                placeholder="Time zone"
              />
              <p className="text-tiny text-text-muted">
                Optional. Leave blank if nothing is booked. The time is read in the zone shown.
              </p>
            </div>

            {/* The portal gate. Worth its own explanation: it is the only field on this form
                that grants somebody outside the company access to anything. */}
            <div className="grid gap-1.5 rounded-lg bg-surface-sunken p-4">
              <Label htmlFor="cl-domains" className="microlabel">
                Portal domains
              </Label>
              <AllowedDomainsField
                id="cl-domains"
                value={allowedDomains}
                onChange={setAllowedDomains}
              />
              <p className="text-tiny text-text-muted">
                A company-owned domain such as <span className="num">cnx.com</span> may belong to
                one client only. Shared/public providers such as Gmail or Yahoo are never allowed
                here; add the person under Contacts and enable <strong>Portal access</strong> for
                their exact email instead.
              </p>
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Geographies</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-tiny"
                  onClick={() => setGeographies([...geographies, { name: '', mode: 'live', address: '' }])}
                >
                  <Plus className="size-3" />
                  Add geography
                </Button>
              </div>
              <p className="text-tiny text-text-muted">
                Where this client is worked with. A project picks one, and its work items
                inherit it.
              </p>

              {existingGeographies.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-xs"
                >
                  {g.mode === 'virtual' ? (
                    <Video className="size-3.5 shrink-0 text-text-muted" aria-hidden />
                  ) : (
                    <MapPin className="size-3.5 shrink-0 text-text-muted" aria-hidden />
                  )}
                  <span className="font-medium">{g.name}</span>
                  <span className="truncate text-text-muted">
                    {g.mode === 'virtual' ? 'Virtual — over a call' : (g.address ?? 'Live')}
                  </span>
                </div>
              ))}

              {geographies.map((g, index) => (
                <div key={index} className="grid gap-2 rounded-md border border-border bg-surface-sunken p-2.5">
                  <div className="flex items-center gap-2">
                    <Input
                      value={g.name}
                      onChange={(e) =>
                        setGeographies(
                          geographies.map((row, i) => (i === index ? { ...row, name: e.target.value } : row)),
                        )
                      }
                      placeholder="CNX Philippines"
                      aria-label="Geography name"
                      className="h-8 text-xs"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-text-muted"
                      aria-label="Remove geography"
                      onClick={() => setGeographies(geographies.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                  <div className="flex gap-1.5">
                    {(['live', 'virtual'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        aria-pressed={g.mode === m}
                        onClick={() =>
                          setGeographies(geographies.map((row, i) => (i === index ? { ...row, mode: m } : row)))
                        }
                        className={cn(
                          'h-8 flex-1 rounded-md border text-xs transition-colors dur-fast',
                          g.mode === m
                            ? 'border-brand bg-brand-soft font-medium text-brand-dark'
                            : 'border-border bg-card text-text-secondary hover:bg-muted',
                        )}
                      >
                        {m === 'live' ? 'Live — somewhere to go' : 'Virtual — over a call'}
                      </button>
                    ))}
                  </div>
                  {g.mode === 'live' ? (
                    <Input
                      value={g.address}
                      onChange={(e) =>
                        setGeographies(
                          geographies.map((row, i) => (i === index ? { ...row, address: e.target.value } : row)),
                        )
                      }
                      placeholder="Exact address — building, floor, street"
                      aria-label="Address"
                      className="h-8 text-xs"
                    />
                  ) : null}
                </div>
              ))}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Contacts</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-tiny"
                  onClick={() => setContacts([...contacts, { name: '', role: '', email: '', portalAccess: false }])}
                >
                  <Plus className="size-3" />
                  Add contact
                </Button>
              </div>
              {contacts.length === 0 ? (
                <p className="text-tiny text-text-muted">
                  The people on their side we deal with. A work item can name one as who asked.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {contacts.map((contact, index) => (
                    // Index-keyed on purpose: a contact has no id until it is saved, and the
                    // name is the field being typed into, so keying on it remounts every keystroke.
                    <li key={index} className="grid gap-2 rounded-md border border-border bg-surface-sunken p-2.5">
                      <div className="flex items-center gap-2">
                        <Input
                          value={contact.name}
                          onChange={(e) =>
                            setContacts(
                              contacts.map((c, i) => (i === index ? { ...c, name: e.target.value } : c)),
                            )
                          }
                          placeholder="Name"
                          maxLength={120}
                          aria-label={`Contact ${index + 1} name`}
                          className="flex-1"
                        />
                        <Input
                          value={contact.role}
                          onChange={(e) =>
                            setContacts(
                              contacts.map((c, i) => (i === index ? { ...c, role: e.target.value } : c)),
                            )
                          }
                          placeholder="Role"
                          maxLength={120}
                          aria-label={`Contact ${index + 1} role`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-9 shrink-0"
                          aria-label={`Remove contact ${index + 1}`}
                          onClick={() => setContacts(contacts.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          type="email"
                          value={contact.email ?? ''}
                          onChange={(e) =>
                            setContacts(
                              contacts.map((c, i) => (i === index ? { ...c, email: e.target.value } : c)),
                            )
                          }
                          placeholder="person@client.com"
                          maxLength={320}
                          aria-label={`Contact ${index + 1} email`}
                          className="flex-1"
                        />
                        <label className="flex shrink-0 items-center gap-2 text-xs text-text-secondary">
                          <Checkbox
                            checked={contact.portalAccess === true}
                            onCheckedChange={(checked) =>
                              setContacts(
                                contacts.map((c, i) => (i === index ? { ...c, portalAccess: checked === true } : c)),
                              )
                            }
                            aria-label={`Allow contact ${index + 1} client portal access`}
                          />
                          Portal access
                        </label>
                      </div>
                      {contact.portalAccess && !contact.email?.trim() ? (
                        <p className="text-tiny text-danger">Enter this contact’s email to grant portal access.</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="cl-notes">Notes</Label>
              <Textarea
                id="cl-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything a newcomer to this account needs to know."
                className="text-sm"
              />
            </div>
          </div>

          <DialogFooter className="border-t border-border px-6 py-4">
            {tried && !complete ? (
              <p className="mr-auto self-center text-tiny text-danger">
                Fill in the required fields.
              </p>
            ) : save.error ? (
              // Verbatim. The backend names the client already holding a domain, and
              // paraphrasing that into "Something went wrong" throws away the fix.
              <p className="mr-auto self-center text-tiny text-danger" role="alert">
                {getErrorMessage(save.error)}
              </p>
            ) : null}
            {!client ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (userId) {
                    saveLocalDraft<ClientDraftPayload>({
                      id: draftId,
                      kind: 'client',
                      userId,
                      workspaceId,
                      title: name.trim() || 'Untitled client',
                      updatedAt: new Date().toISOString(),
                      payload: { name, stage, accountOwnerId, nextMeetingAt, meetingZone, contacts, geographies, allowedDomains, notes },
                    })
                  }
                  setOpen(false)
                  onDraftClosed?.()
                  setDraftId(newDraftId())
                }}
              >
                Keep draft &amp; close
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : client ? 'Save changes' : 'Add client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Required() {
  return (
    <span className="text-danger" aria-hidden>
      *
    </span>
  )
}

function Hint({ show, children }: { show: boolean; children: ReactNode }) {
  return show ? <p className="text-tiny text-danger">{children}</p> : null
}
